<!-- TODO: refactor to MapLayer and MapSource vue components -->
<script setup>
import {computed, onBeforeUnmount, ref, watchEffect} from 'vue'
import {useStore} from 'vuex'
import {useRouter} from 'vue-router'
import { v4 as uuidv4 } from 'uuid'
import {bbox} from '@turf/turf'

import {Trash2, Eye, Upload, Edit2, FolderOpen} from 'lucide-vue-next'
import {useToast} from '@/components/ui/toast/use-toast'
import {Button} from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BulkUploadTable, BulkEditDialog, UploadProgressDialog } from '@/components/bulk-upload'

import {parseKML, parseKMZ, parseGeoJSON, parseImage, parseGPX, parseFIT} from '@/composables/files'

const { toast } = useToast()
const router = useRouter()
const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const loadedFiles = computed(() => store.getters['geo/getFiles'])
const projects = computed(() => store.getters['project/getFlatedProjects'])
const currentProject = computed(() => store.getters['project/getProject'])
const selectedOrgId = computed(() => store.getters['org/getSelected'])

const MAX_FILES = 30

const isUploading = ref(false)
const selectedFeatures = ref([])
const showBulkEditDialog = ref(false)
const showUploadProgressDialog = ref(false)
const uploadProgressRef = ref(null)
const selectedProjectId = ref(null)

const collection = ref({
  type: 'FeatureCollection',
  features: []
})

const activeLayers = []
const activeSources = []

const setCollection = (fileCollection, fileName) => {
  createLayers()

  for (let feature of fileCollection.features) {
    // Preserve existing properties from the parsed file
    feature.properties = {
      ...feature.properties, // Keep all existing properties including description, name, etc.
      fileName: fileName,
      // Only set name as fallback if it doesn't exist
      name: feature.properties.name || feature.properties.Name || fileName.split('.').shift(),
      // Preserve description if it exists and convert to string
      description: (() => {
        const desc = feature.properties.description || feature.properties.Description || feature.properties.desc
        return typeof desc === 'string' ? desc : (desc ? JSON.stringify(desc) : '')
      })(),
      local_uuid: uuidv4(),
      isEditing: false
    }
    collection.value.features.push(feature)
  }

  const sourceNoActiveFeature = collection.value.features.filter(f => !f.properties.isEditing)
  map.value.getSource('temp_data').setData({
    type: 'FeatureCollection',
    features: sourceNoActiveFeature
  })
}

const splitFeature = (feature) => {
  collection.value.features = collection.value.features.filter(f => f.properties.local_uuid !== feature.properties.local_uuid)

  if (feature.geometry?.type === 'GeometryCollection') {
    for (let geometry of feature.geometry.geometries) {
      const geoFeature = {
        type: 'Feature',
        geometry: geometry,
        properties: {
          ...feature.properties ? feature.properties : {},
          // Preserve the original name and description
          name: feature.properties.name || feature.properties.fileName?.split('.').shift() || 'Unnamed',
          description: feature.properties.description || '',
          fileName: feature.properties.fileName,
          local_uuid: uuidv4(),
          isEditing: false
        }
      }
      collection.value.features.push(geoFeature)
    }
  }
}

const handleFile = async (file) => {
  const ext = file.name.split('.').pop().toLowerCase()
  let collection, name

  switch (ext) {
    case 'kml':
      ({ collection, name } = await parseKML(file))
      break
    case 'kmz':
      ({ collection, name } = await parseKMZ(file))
      break
    case 'geojson':
      ({ collection, name } = await parseGeoJSON(file))
      break
    case 'gpx':
      ({ collection, name } = await parseGPX(file))
      break
    case 'fit':
      ({ collection, name } = await parseFIT(file))
      console.log(collection, name)
      break
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'heic':
    case 'heif':
    case 'avif':
    case 'webp':
      ({ collection, name } = await parseImage(file))
      break
    default:
      toast({ title: 'Invalid file type', status: 'error' })
      break
  }

  if (collection && name) {
    setCollection(collection, name)
  }
}

const handleFiles = async (files) => {
  if (!files) return
  if (files.length === 0) return

  if (files.length > MAX_FILES) {
    toast({title: `Only ${MAX_FILES} files can be uploaded at once`})
    return
  }

  for (let i = 0; i < files.length; i++) {
    if (i > MAX_FILES) break
    await handleFile(files[i])
  }
}

const createLayers = () => {
  if (!map.value.getSource('temp_data')) {
    map.value.addSource('temp_data', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      },
    });
    activeLayers.push('temp_data');
  }

  if (!map.value.getLayer('temp-geometry-layer-fill')) {
    map.value.addLayer({
      'id': 'temp-geometry-layer-fill',
      'type': 'line',
      'source': 'temp_data',
      'paint': {
        'line-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'line-width': 4
      },
      'filter': ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']]
    });
    activeLayers.push('temp-geometry-layer-fill');
  }

  if (!map.value.getLayer('temp-geometry-layer-lines')) {
    map.value.addLayer({
      'id': 'temp-geometry-layer-lines',
      'type': 'fill',
      'source': 'temp_data',
      'paint': {
        'fill-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'fill-opacity': 0.03
      },
      'filter': ['==', '$type', 'Polygon']
    });
    activeLayers.push('temp-geometry-layer-lines');
  }

  if (!map.value.getLayer('temp-geometry-layer-points')) {
    map.value.addLayer({
      id: 'temp-geometry-layer-points',
      type: 'circle',
      source: 'temp_data',
      paint: {
        'circle-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      },
      'filter': ['==', '$type', 'Point']
    });
    activeLayers.push('temp-geometry-layer-points');
  }

  if (!map.value.getSource('temp_data')) return
  map.value.getSource('temp_data').setData(collection.value)
  activeSources.push('temp_data')
}

const focusCollection = () => {
  const bounds = bbox(collection.value)
  map.value.fitBounds(bounds, { padding: 20 })
}

const removeFeature = async (feature) => {
  collection.value.features = collection.value.features.filter(f => f !== feature)
  if (!map.value.getSource('temp_data')) return
  map.value.getSource('temp_data').setData(collection.value)

  if (collection.value.features.length === 0) {

    if (feature.properties?.code) {
          // Use the new createFeature function instead
    await createFeature(feature)
    }
  }
}

const clearCollection = () => {
  collection.value.features = []
  selectedFeatures.value = []
  if (!map.value.getSource('temp_data')) return
  map.value.getSource('temp_data').setData(collection.value)
}

const startEditing = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.properties.isEditing = !f.properties.isEditing
    } else {
      f.properties.isEditing = false
    }
  }
  const collectionNoActive = {
    type: 'FeatureCollection',
    features: collection.value.features.filter(f => !f.properties.isEditing)
  }
  map.value.getSource('temp_data').setData(collectionNoActive)
}

const stopEditing = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.geomertry = feature.geometry
      f.properties.isEditing = false
    }
  }
  if (!map.value.getSource('temp_data')) return
  map.value.getSource('temp_data').setData(collection.value)
}

const updateFeature = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.properties = feature.properties
    }
  }

  if (!map.value.getSource('temp_data')) return
  map.value.getSource('temp_data').setData(collection.value)
}

const reset = () => {
  for (let layer of activeLayers) {
    if (map.value.getLayer(layer)) map.value.removeLayer(layer)
  }

  for (let source of activeSources) {
    if (map.value.getSource(source)) map.value.removeSource(source)
  }

  collection.value.features = []
  selectedFeatures.value = []
  store.commit('geo/SET_FILES', null)
}

// New bulk upload methods
const handleSelectFeatures = (features) => {
  selectedFeatures.value = features
}

const handlePreviewFeature = (feature) => {
  const bounds = bbox(feature)
  map.value.fitBounds(bounds, { padding: 20 })
}

const handleEditFeature = (feature) => {
  // For now, just focus on the feature
  handlePreviewFeature(feature)
  // TODO: Implement individual feature editing if needed
}

const handleDeleteFeature = (feature) => {
  removeFeature(feature)
}

const handleBulkEdit = () => {
  showBulkEditDialog.value = true
}

const handleBulkEditApply = (changes) => {
  // Apply changes to selected features
  selectedFeatures.value.forEach(feature => {
    // Apply name changes
    if (changes.nameOperation) {
      const currentName = feature.properties.name || feature.properties.fileName || 'Unnamed'

      switch (changes.nameOperation) {
        case 'replace':
          feature.properties.name = changes.nameValue
          break
        case 'prefix':
          feature.properties.name = `${changes.nameValue}${currentName}`
          break
        case 'suffix':
          feature.properties.name = `${currentName}${changes.nameValue}`
          break
      }
    }

    // Apply description changes
    if (changes.descriptionOperation) {
      const currentDescription = feature.properties.description || ''

      switch (changes.descriptionOperation) {
        case 'replace':
          feature.properties.description = changes.descriptionValue
          break
        case 'append':
          feature.properties.description = currentDescription + changes.descriptionValue
          break
      }
    }

    // Apply other changes
    if (changes.colorValue) {
      feature.properties.color = changes.colorValue
    }

    if (changes.visibilityValue !== undefined) {
      feature.properties.shared = changes.visibilityValue
    }

    if (changes.projectValue) {
      feature.properties.project_id = changes.projectValue
    }

    if (changes.staticValue !== undefined) {
      feature.properties.is_static = changes.staticValue
    }
  })

  // Update map
  if (map.value.getSource('temp_data')) {
    map.value.getSource('temp_data').setData(collection.value)
  }

  toast({
    title: `Updated ${selectedFeatures.value.length} features`,
  })
}

const handleUploadSelected = async () => {
  const featuresToUpload = selectedFeatures.value.length > 0 ? selectedFeatures.value : collection.value.features

  if (featuresToUpload.length === 0) {
    toast({ title: 'No features to upload' })
    return
  }

  // Check if a project is selected
  const projectId = selectedProjectId.value || currentProject.value?.project_id
  if (!projectId) {
    toast({ title: 'Please select a project first', variant: 'destructive' })
    return
  }

  showUploadProgressDialog.value = true
  isUploading.value = true

  // Process uploads one by one
  for (let i = 0; i < featuresToUpload.length; i++) {
    const feature = featuresToUpload[i]

    try {
      // Update progress
      uploadProgressRef.value?.updateFeatureStatus(feature.properties.local_uuid, 'uploading', null, 0)

      // Prepare feature data for upload
      const featureData = {
        type: 'Feature',
        properties: {
          name: feature.properties.name || feature.properties.fileName || 'Unnamed',
          description: feature.properties.description || '',
          shared: feature.properties.shared || false,
          is_static: feature.properties.is_static || false,
          member_id: store.getters['user/getMember']?.member_id,
          created_origin: Date.now(),
          type: feature.geometry.type,
          subtype: 'report',
          color: feature.properties.color || '#0059ff',
          project_id: feature.properties.project_id || projectId,
          data: []
        },
        geometry: feature.geometry
      }

      // Log feature being uploaded (helps verify description preservation)
      console.log(`Uploading feature "${featureData.properties.name}" with description: "${featureData.properties.description}"`)

      // Create the feature
      const result = await store.dispatch('geo/saveFeature', {
        project_id: feature.properties.project_id || projectId,
        files: [], // No files for bulk upload
        feature: featureData
      })

      // Update progress to success
      uploadProgressRef.value?.updateFeatureStatus(feature.properties.local_uuid, 'success')

      // Remove from collection
      collection.value.features = collection.value.features.filter(f => f.properties.local_uuid !== feature.properties.local_uuid)

    } catch (error) {
      console.error('Error uploading feature:', error)
      uploadProgressRef.value?.updateFeatureStatus(feature.properties.local_uuid, 'error', error.message || 'Upload failed')
    }
  }

  // Update map
  if (map.value.getSource('temp_data')) {
    map.value.getSource('temp_data').setData(collection.value)
  }

  isUploading.value = false
  selectedFeatures.value = []

  toast({
    title: `Upload completed`,
  })
}

const handleUploadCancel = () => {
  isUploading.value = false
  showUploadProgressDialog.value = false
}

const handleUploadRetry = (failedFeatures) => {
  // Add failed features back to collection if they were removed
  failedFeatures.forEach(feature => {
    if (!collection.value.features.find(f => f.properties.local_uuid === feature.properties.local_uuid)) {
      collection.value.features.push(feature)
    }
  })

  // Update map
  if (map.value.getSource('temp_data')) {
    map.value.getSource('temp_data').setData(collection.value)
  }

  showUploadProgressDialog.value = false
}

const handleValidateFeature = (feature) => {
  // Update map with modified feature
  if (map.value.getSource('temp_data')) {
    map.value.getSource('temp_data').setData(collection.value)
  }
}

const handleUpdateFeature = (feature) => {
  // Find and update the feature in the collection
  const index = collection.value.features.findIndex(f => f.properties.local_uuid === feature.properties.local_uuid)
  if (index !== -1) {
    collection.value.features[index] = feature
  }

  // Update map
  if (map.value.getSource('temp_data')) {
    map.value.getSource('temp_data').setData(collection.value)
  }
}

// Legacy method - now using handleUploadSelected instead

const createFeature = async (feature) => {
  try {
    const result = await store.dispatch('geo/createFeature', {
      geojson: feature,
      project_id: feature.properties.project_id
    })

    // router.push({name: 'ProjectHome', params: {project_id: feature.properties.project_id}})
    router.push({
      name: 'Feature',
      params: {
        org_id: feature.properties.organization_id,
        project_id: feature.properties.project_id,
        featureCode: feature.properties.code
      }
    })
  } catch (error) {
    console.error('Error creating feature:', error)
  }
}

// Initialize with current project if available
watchEffect(() => {
  if (currentProject.value && !selectedProjectId.value) {
    selectedProjectId.value = currentProject.value.project_id
  }
})

// Reload projects when organization changes
watchEffect(async () => {
  if (selectedOrgId.value) {
    // Fetch projects for the current organization
    await store.dispatch('project/fetchProjects')

    // Reset selected project if it's not in the new org's projects
    if (selectedProjectId.value && projects.value) {
      const projectExists = projects.value.some(p => p.project_id === selectedProjectId.value)
      if (!projectExists) {
        selectedProjectId.value = null
      }
    }
  }
})

watchEffect(async () => {
  await handleFiles(loadedFiles.value)
})

onBeforeUnmount(() => {
  reset()
})
</script>

<template>
  <div class="bg-white">
    <div v-if="collection.features.length > 0" class="p-4">
      <!-- Project Selector -->
      <div class="mb-6">
        <div class="flex items-center space-x-4">
          <div class="flex-1 max-w-sm">
            <Select v-model="selectedProjectId">
              <SelectTrigger>
                <SelectValue
                  :placeholder="currentProject ? `Current: ${currentProject.project_name}` : 'Select a project'"
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="project in projects.filter(p => parseInt(p.organization_id) === parseInt(selectedOrgId))"
                  :key="project.project_id"
                  :value="project.project_id"
                >
                  {{ project.project_name }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
         </div>
       </div>

      <BulkUploadTable
        :features="collection.features"
        :is-uploading="isUploading"
        :selected-project="selectedProjectId ? projects.find(p => p.project_id === selectedProjectId) : currentProject"
        @select-features="handleSelectFeatures"
        @preview-feature="handlePreviewFeature"
        @edit-feature="handleEditFeature"
        @delete-feature="handleDeleteFeature"
        @bulk-edit="handleBulkEdit"
        @upload-selected="handleUploadSelected"
        @validate-feature="handleValidateFeature"
        @update-feature="handleUpdateFeature"
      />
    </div>
    <div v-else class="text-center py-12">
      <Upload class="w-10 h-10 mx-auto mb-4" />
      <h4 class="mb-1 text-lg">
        Drag and drop files on the map
      </h4>
      <p class="text-xs text-gray-400 mb-4">
        Supported formats: KML, KMZ, GeoJSON, GPX, FIT, and images with GPS data
      </p>
    </div>

    <!-- Bulk Edit Dialog -->
    <BulkEditDialog
      :open="showBulkEditDialog"
      :features="selectedFeatures"
      :projects="projects"
      @close="showBulkEditDialog = false"
      @apply="handleBulkEditApply"
    />

    <!-- Upload Progress Dialog -->
    <UploadProgressDialog
      ref="uploadProgressRef"
      :open="showUploadProgressDialog"
      :features="selectedFeatures.length > 0 ? selectedFeatures : collection.features"
      :is-uploading="isUploading"
      @close="showUploadProgressDialog = false"
      @cancel="handleUploadCancel"
      @retry="handleUploadRetry"
    />
  </div>
</template>
